import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { WEBSITE_LOCAL_PAGES, localPreposition } from '../../data/websiteLocalPages';
import type { WebsiteLocalPage } from '../../data/websiteLocalPages';
import { PRIMARY_CTA_CLASS, SECONDARY_CTA_CLASS } from './ctaStyles';
import { OfferFormCta, OfferWhatsAppCta } from './OfferCtaButtons';
import { HERO_BADGES, MONTHLY_PRICE, SETUP_PRICE, VAT_MENTION, formatEuro } from './offerData';

interface PageProps {
  page: WebsiteLocalPage;
}

export const WebsiteLocalHero: React.FC<PageProps> = ({ page }) => (
  <section className="section-spacing relative overflow-hidden bg-canvas border-t border-line">
    <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: 'easeOut' }}
      >
        <p className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent-strong/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-accent-soft mb-4">
          <MapPin className="h-3.5 w-3.5" aria-hidden="true" /> {page.shortName}
        </p>
        <h1 className="heading-display text-balance mb-4">{page.h1}</h1>
        <p className="text-xl md:text-2xl font-semibold text-fg mb-3">
          <span className="text-accent-soft">{formatEuro(SETUP_PRICE)}</span> à la mise en ligne, puis{' '}
          <span className="text-accent-soft">{formatEuro(MONTHLY_PRICE)}/mois</span>
        </p>
        <p className="text-fg-muted text-sm mb-6">Sans acompte · {VAT_MENTION}</p>
        <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
          {HERO_BADGES.map(({ label, icon: Icon }) => (
            <span key={label} className="flex items-center gap-1.5 bg-white/10 border border-line-strong rounded-full px-4 py-1.5 text-sm text-fg">
              <Icon className="h-4 w-4 text-accent-soft" aria-hidden="true" /> {label}
            </span>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4">
          <OfferWhatsAppCta location={`local_${page.slug}_hero`} className={`${PRIMARY_CTA_CLASS} w-full sm:w-auto`} />
          <OfferFormCta location={`local_${page.slug}_hero`} className={`${SECONDARY_CTA_CLASS} w-full sm:w-auto`} />
        </div>
      </motion.div>
    </div>
  </section>
);

export const WebsiteLocalIntro: React.FC<PageProps> = ({ page }) => (
  <section className="section-spacing bg-canvas">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="heading-section text-balance mb-5">
        Un site internet pour votre activité, {localPreposition(page)}
      </h2>
      <div className="space-y-4 text-fg-muted leading-relaxed">
        {page.intro.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </div>
    </div>
  </section>
);

export const WebsiteLocalNearby: React.FC<PageProps> = ({ page }) => {
  const otherPages = WEBSITE_LOCAL_PAGES.filter((entry) => entry.slug !== page.slug);
  return (
    <section className="section-spacing bg-canvas border-t border-line">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-balance text-2xl font-bold text-fg mb-4 text-center">Nous intervenons aussi à</h2>
        <ul className="flex flex-wrap justify-center gap-2 mb-8">
          {page.nearbyTowns.map((town) => (
            <li key={town} className="rounded-full border border-line-strong bg-white/5 px-3 py-1 text-sm text-fg">
              {town}
            </li>
          ))}
        </ul>
        <ul className="flex flex-col items-center gap-2">
          {otherPages.map((entry) => (
            <li key={entry.path}>
              <Link to={entry.path} className="inline-flex min-h-11 items-center gap-1.5 text-sm text-accent-soft hover:text-fg transition-colors md:min-h-0">
                <ArrowRight className="h-4 w-4" aria-hidden="true" /> Création de site internet {localPreposition(entry)}
              </Link>
            </li>
          ))}
          <li>
            <Link to="/creation-site-web" className="inline-flex min-h-11 items-center gap-1.5 text-sm text-accent-soft hover:text-fg transition-colors md:min-h-0">
              <ArrowRight className="h-4 w-4" aria-hidden="true" /> Découvrir toute l'offre de création de site internet
            </Link>
          </li>
        </ul>
      </div>
    </section>
  );
};
